import React, { MouseEventHandler, ReactNode, useEffect, useImperativeHandle, useRef, useState } from "react";

export interface ActionSheetProps {
    onClose?: () => void;
    children?: ReactNode;
    mouseEnable?: boolean;
    touchEnable?: boolean;
    threshold?: number;
    opacity?: number;
    zIndex?: number;
    closeOnBgTap?: boolean;
    bgTransition?: string;
    className?: string;
    sheetTransition?: string;
  }
  
  export interface ActionSheetRef {
    open(): void;
    close(): void;
  }

const ActionSheet = React.forwardRef<
  ActionSheetRef | undefined,
  ActionSheetProps
>(
  (
    {
      onClose,
      children,
      mouseEnable = true,
      touchEnable = true,
      threshold = 50,
      opacity = 1,
      zIndex = 998,
      closeOnBgTap = true,
      bgTransition = "opacity 0.3s ease-in-out, z-index 0.3s ease-in-out",
      className = "action-sheet",
      sheetTransition = "transform 0.3s linear",
    },
    ref
  ): JSX.Element => {
    const [show, setShow] = useState(false);
    const pressed = useRef(false);
    const sheetRef = useRef<HTMLDivElement>(null);
    const animationRef = useRef(0);
    const masterOffset = useRef(0);
    const startY = useRef(0);

    useImperativeHandle(ref, () => ({
      open(): void {
        setShow(true);
      },
      close(): void {
        setShow(false);
      },
    }));

    const BgClick = (): void => {
      setShow(false);
      if (onClose) onClose();
    };

    const requestSheetDown = React.useCallback((): boolean => {
      if (null !== sheetRef.current) {
        sheetRef.current.style.transition = sheetTransition;
        sheetRef.current.style.transform = "translate3d(-50%, 101%, 0)";
        return true;
      }
      return false;
    }, [sheetTransition]);

    const requestSheetUp = React.useCallback((): boolean => {
      if (null !== sheetRef.current) {
        sheetRef.current.style.transform = `translate3d(-50%, 0%, 0)`;
        return true;
      }
      return false;
    }, []);

    useEffect(() => {
      if (show) {
        requestSheetUp();
      } else {
        requestSheetDown();
      }
    }, [requestSheetDown, requestSheetUp, show]);

    const onSwipeMove = (event: React.TouchEvent<HTMLDivElement>): void => {
      if (pressed.current) {
        const offset = event.touches[0].clientY - startY.current;
        move(offset);
      }
    };

    const onMouseMove = (
      event: React.MouseEvent<HTMLDivElement, MouseEvent>
    ): void => {
      event.stopPropagation();
      if (pressed.current) {
        const offset = event.clientY - startY.current;
        move(offset);
      }
    };

    const move = (offset: number): boolean => {
      if (offset > 0) {
        masterOffset.current = offset;
        animationRef.current = requestAnimationFrame(updatePosition);
        return true;
      }
      return false;
    };

    const updatePosition = (): boolean => {
      if (animationRef.current !== undefined) {
        if (null !== sheetRef.current) {
          sheetRef.current.style.transform = `translate3d(-50%, ${masterOffset.current}px, 0)`;
          return true;
        }
        return false;
      }
      return false;
    };

    const onSwipeStart = (event: React.TouchEvent<HTMLDivElement>): void => {
      if (sheetRef?.current) sheetRef.current.style.transition = "none";
      startY.current = event.touches[0].clientY;
      changePressed(true);
    };

    const onMouseStart = (
      event: React.MouseEvent<HTMLDivElement, MouseEvent>
    ): void => {
      if (sheetRef?.current) sheetRef.current.style.transition = "none";
      startY.current = event.clientY;
      changePressed(true);
    };

    const changePressed = (x: boolean): void => {
      pressed.current = x;
    };

    const onSwipeEnd = (): void => {
      cancelAnimationFrame(animationRef.current);
      changePressed(false);
      if (Math.abs(masterOffset.current) > threshold) {
        setShow(false);
        if (onClose) onClose();
      } else {
        requestSheetUp();
      }
      masterOffset.current = 0;
    };

    const onClickUnderlay: MouseEventHandler = (e) => {
        e.stopPropagation()
        e.preventDefault()
        if (closeOnBgTap) {
            BgClick?.()
        }
    }

    return (
      <>
        <div
          onClick={onClickUnderlay}
          className={className}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            backfaceVisibility: "hidden",
            transition: bgTransition,
            opacity: show ? opacity : 0,
            zIndex: show ? zIndex : -1,
          }}
        ></div>
        <div
          ref={sheetRef}
          style={{
            overflowX: "hidden",
            position: "fixed",
            left: '50%',
            bottom: 0,
            transform: "translate3d(-50%, 101%, 0)",
            width: "100%",
            backgroundColor: "#fbfbfb",
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            touchAction: "none",
            zIndex: zIndex + 1,
            maxWidth: '450px',
          }}
          onMouseDown={mouseEnable ? onMouseStart : () => undefined}
          onMouseMove={mouseEnable ? onMouseMove : () => undefined}
          onMouseUp={mouseEnable ? onSwipeEnd : () => undefined}
          onTouchStart={touchEnable ? onSwipeStart : () => undefined}
          onTouchMove={touchEnable ? onSwipeMove : () => undefined}
          onTouchEnd={touchEnable ? onSwipeEnd : () => undefined}
        >
          {children}
        </div>
      </>
    );
  }
);

export default ActionSheet;